/**
 * Web adapter — implements IElectronAPI using HTTP REST + WebSocket.
 * Replaces Electron IPC with standard web protocols.
 */

import { uploadLegacyFileViaHostInputs } from '../services/legacyHostInputUpload';
import { isHostInputRejection } from '../services/hostInputTransfer';
import { detectWebClientRuntime } from '../../shared/webClientRuntime';
import { getAuthSessionScope } from '../services/authSessionScope';

const API_BASE = '/api';

function getToken(): string | null {
  return localStorage.getItem('boclaw_token');
}

/** clientRuntimeHeaders 提供浏览器允许读取的非敏感运行环境提示。 */
function clientRuntimeHeaders(): Record<string, string> {
  const userAgentData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  const runtime = detectWebClientRuntime({
    userAgent: navigator.userAgent,
    platform: userAgentData?.platform || navigator.platform,
    language: navigator.language,
  });
  const memoryGB = Number((navigator as Navigator & { deviceMemory?: number }).deviceMemory) || 0;
  return {
    'X-Boclaw-Client-OS': runtime.os,
    'X-Boclaw-Client-OS-Version': runtime.osVersion,
    'X-Boclaw-Client-Arch': runtime.arch,
    'X-Boclaw-Client-Locale': runtime.locale,
    'X-Boclaw-Client-Browser': runtime.browserName,
    'X-Boclaw-Client-Browser-Version': runtime.browserVersion,
    'X-Boclaw-Client-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    'X-Boclaw-Client-Memory-MB': String(Math.max(0, Math.round(memoryGB * 1024))),
  };
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  const userId = localStorage.getItem('boclaw_user_id');
  const role = localStorage.getItem('boclaw_user_role');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...clientRuntimeHeaders(),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (userId) headers['X-Boclaw-User-Id'] = userId;
  if (role) headers['X-Boclaw-User-Role'] = role;
  return headers;
}

// 防止多个并发 401 响应同时触发多次 logout
let isLoggingOut = false;

async function handleUnauthorized(path: string, isCurrent: () => boolean = () => true): Promise<void> {
  if (path.startsWith('/auth/') || isLoggingOut || !isCurrent()) return;
  if (!getToken()) return;

  // CE 不走 refresh token 路线：Platform 端 session 已通过 token_version
  // 主动吊销 + 滑动续期处理长期登录态。收到 401 只需要清本地登出，
  // 不再尝试 /api/user/token/refresh（该接口在 Platform 上并不存在）。
  isLoggingOut = true;
  try {
    const { authService } = await import('../services/auth');
    const { i18nService } = await import('../services/i18n');
    if (!isCurrent()) return;
    await authService.logout();
    // persistent: true —— 用户关掉才关掉，符合"被下线必须明确告知"的要求
    window.dispatchEvent(new CustomEvent('app:showToast', {
      detail: { message: i18nService.t('authTokenExpired'), persistent: true },
    }));
  } finally {
    isLoggingOut = false;
  }
}

async function apiFetch(path: string, options: RequestInit = {}): Promise<any> {
  // REST 响应必须属于发起时的登录周期；旧 401 不能清理新用户会话。
  const token = getToken(), scope = getAuthSessionScope();
  const isCurrent = () => scope === getAuthSessionScope();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  if (!isCurrent()) throw new Error('Account changed');
  if (!res.ok) {
    if (res.status === 401) {
      // 旧凭证的失败不能登出已经成功续期的会话；当前凭证仍按原有逻辑处理吊销。
      await handleUnauthorized(path, () => isCurrent() && token === getToken());
    }
    const body = await res.json().catch(() => ({}));
    // 结构化字段（code / available / 状态码）必须随错误一起往上传：调用方要靠 code 区分
    // "模型不可用" 这类可自助恢复的拒绝，并把 available 渲染成切换引导。只抛一句文本
    // 会让上层除了打印之外什么都做不了 —— 用户界面上就表现为“没有任何反应”。
    const err = new Error(body.message || body.error || `HTTP ${res.status}`) as Error & {
      code?: string; available?: string[]; status?: number; modelId?: string;
    };
    err.status = res.status;
    if (typeof body.code === 'string') err.code = body.code;
    if (Array.isArray(body.available)) err.available = body.available;
    if (typeof body.modelId === 'string') err.modelId = body.modelId;
    throw err;
  }
  const value = await res.json();
  if (!isCurrent()) throw new Error('Account changed');
  return value;
}

/** 交互请求有界等待；写入取消只表示未确认，不能认定服务端已撤销。 */
async function boundedApiFetch<T = unknown>(path: string, options: RequestInit = {}, timeoutMs = 20_000): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await apiFetch(path, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
}

// WebSocket connection with auto-reconnect
let ws: WebSocket | null = null;
let wsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
let wsCurrentUserId: string | null = null; // track userId used for current WS connection
let wsCurrentScope: string | null = null;
let wsHasConnected = false;
const wsListeners = new Map<string, Set<(data: any) => void>>();

function connectWebSocket(): void {
  const token = getToken();
  const userId = localStorage.getItem('boclaw_user_id');
  const scope = getAuthSessionScope();

  // If already connected but userId changed (login/logout/switch user), force reconnect
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    if (userId !== wsCurrentUserId || scope !== wsCurrentScope) {
      console.log(`[WS] userId changed (${wsCurrentUserId} → ${userId}), reconnecting...`);
      ws.close();
      return; // onclose will trigger reconnect with new credentials
    }
    return;
  }

  wsCurrentUserId = userId;
  wsCurrentScope = scope;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (userId) params.set('userId', userId);
  const qs = params.toString();
  const url = `${protocol}//${window.location.host}/ws${qs ? `?${qs}` : ''}`;

  const connection = new WebSocket(url);
  ws = connection;
  // 旧连接的无 userId 事件同样必须丢弃；同账号重新登录也属于新的凭据周期。
  const isCurrentConnection = () => ws === connection && getAuthSessionScope() === scope
    && localStorage.getItem('boclaw_user_id') === userId;

  connection.onopen = () => {
    if (!isCurrentConnection()) { connection.close(); return; }
    console.log('[WS] Connected');
    if (wsHasConnected) {
      window.dispatchEvent(new CustomEvent('boclaw:ws-reconnected'));
    }
    wsHasConnected = true;
    if (wsReconnectTimer) {
      clearTimeout(wsReconnectTimer);
      wsReconnectTimer = null;
    }
  };

  connection.onmessage = (event) => {
    if (!isCurrentConnection()) return;
    try {
      const { event: evt, data } = JSON.parse(event.data);
      const eventUserId = typeof data?.userId === 'string' ? data.userId : null;
      const currentUserId = localStorage.getItem('boclaw_user_id');
      if (eventUserId && eventUserId !== currentUserId) {
        console.warn(`[WS] Ignored event for another user: ${evt}`);
        return;
      }
      const listeners = wsListeners.get(evt);
      if (listeners) {
        listeners.forEach((cb) => cb(data));
      }
    } catch (e) {
      console.warn('[WS] Failed to parse message:', e);
    }
  };

  connection.onclose = () => {
    if (ws !== connection) return;
    ws = null;
    if (!wsReconnectTimer) {
      wsReconnectTimer = setTimeout(connectWebSocket, 3000);
    }
  };

  connection.onerror = (err) => {
    if (!isCurrentConnection()) return;
    console.error('[WS] Error:', err);
  };
}

// Force WS reconnect when auth state changes (login/logout/switch user).
// auth.ts updateState() dispatches this event after writing credentials to localStorage.
window.addEventListener('auth:stateChanged', () => {
  connectWebSocket();
});

function onWsEvent(event: string, callback: (data: any) => void): () => void {
  if (!wsListeners.has(event)) {
    wsListeners.set(event, new Set());
  }
  wsListeners.get(event)!.add(callback);

  // Ensure WebSocket is connected
  connectWebSocket();

  return () => {
    const listeners = wsListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) wsListeners.delete(event);
    }
  };
}

export function createWebAdapter(): Window['electron'] {
  return {
    platform: 'web',
    arch: 'web',

    store: {
      get: async (key: string) => {
        const res = await apiFetch(`/store/${encodeURIComponent(key)}`);
        return res.value;
      },
      set: async (key: string, value: any) => {
        await apiFetch(`/store/${encodeURIComponent(key)}`, {
          method: 'PUT',
          body: JSON.stringify({ value }),
        });
      },
      remove: async (key: string) => {
        await apiFetch(`/store/${encodeURIComponent(key)}`, { method: 'DELETE' });
      },
    },

    skills: {
      list: async () => {
        try {
          const skills = await apiFetch('/skills');
          return { success: true, skills };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      setEnabled: async (options: any) => {
        try {
          await apiFetch(`/skills/${options.id}/enabled`, { method: 'PUT', body: JSON.stringify({ enabled: options.enabled }) });
          const skills = await apiFetch('/skills');
          return { success: true, skills };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      setCategory: async (options: any) => {
        try {
          await apiFetch(`/skills/${options.id}/category`, { method: 'PUT', body: JSON.stringify({ category: options.category }) });
          const skills = await apiFetch('/skills');
          return { success: true, skills };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      setTags: async (options: { id: string; tags: string[] }) => {
        try {
          const result = await apiFetch(`/skills/${encodeURIComponent(options.id)}/tags`, {
            method: 'PUT',
            body: JSON.stringify({ tags: options.tags }),
          });
          if (!result?.success || !Array.isArray(result.skills)) {
            return { success: false, error: result?.error || 'Failed to update skill tags' };
          }
          return { success: true, skills: result.skills };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      delete: async (id: any) => {
        try {
          const result = await apiFetch(`/skills/${encodeURIComponent(id)}`, { method: 'DELETE' });
          if (!result?.success) return { success: false, error: result?.error || 'Failed to delete skill' };
          const skills = await apiFetch('/skills');
          return { success: true, skills };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      download: async (source: any) => {
        try {
          const result = await apiFetch('/skills/download', { method: 'POST', body: JSON.stringify({ source }) });
          if (!result?.success) return { success: false, error: result?.error || 'Failed to download skill' };
          const skills = await apiFetch('/skills');
          return { success: true, skills };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      uploadLocal: async (archive: Blob, requestId: string, options: { force?: boolean; skipDuplicates?: boolean } = {}) => {
        try {
          const params = new URLSearchParams({ requestId });
          if (options.force) params.set('force', '1');
          if (options.skipDuplicates) params.set('skipDuplicates', '1');
          return await boundedApiFetch(`/skills/upload?${params}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/zip' },
            body: archive,
          }, 180_000);
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      onImportProgress: (callback: any) => onWsEvent('skills.import.progress', callback),
      getRoot: async () => {
        try {
          const res = await apiFetch('/skills/root');
          return { success: true, path: res.root };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      autoRoutingPrompt: async () => {
        try {
          const res = await apiFetch('/skills/auto-routing-prompt');
          return { success: true, prompt: res.prompt };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      getConfig: async (skillId: any) => {
        try {
          const res = await apiFetch(`/skills/${skillId}/config`);
          return { success: true, config: res.config };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      setConfig: async (skillId: any, config: any) => {
        try {
          await apiFetch(`/skills/${skillId}/config`, { method: 'PUT', body: JSON.stringify({ config }) });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      testEmailConnectivity: async (skillId: any, config: any) => {
        try {
          return await apiFetch('/skills/test-email', { method: 'POST', body: JSON.stringify({ skillId, ...config }) });
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      fetchOnline: async (params: any) => {
        try {
          const searchParams = new URLSearchParams();
          if (params.search) searchParams.set('search', params.search);
          if (params.sort) searchParams.set('sort', params.sort);
          if (params.page) searchParams.set('page', String(params.page));
          if (params.limit) searchParams.set('pageSize', String(params.limit));
          if (params.lang) searchParams.set('lang', params.lang);
          const data = await apiFetch(`/skills/online?${searchParams}`);
          return { success: true, data };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      fetchFeatured: async (params: any) => {
        try {
          const searchParams = new URLSearchParams();
          if (params.search) searchParams.set('search', params.search);
          if (params.page) searchParams.set('page', String(params.page));
          if (params.limit) searchParams.set('pageSize', String(params.limit));
          if (params.lang) searchParams.set('lang', params.lang);
          const data = await apiFetch(`/skills/featured?${searchParams}`);
          return { success: true, data };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      installOnline: async (params: any) => {
        try {
          const result = await apiFetch('/skills/install-online', { method: 'POST', body: JSON.stringify(params) });
          if (!result?.success) return { success: false, error: result?.error || 'Failed to install skill' };
          const skills = await apiFetch('/skills');
          return { success: true, skills };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      onChanged: (callback: any) => onWsEvent('skills.changed', callback),
      // @ts-ignore CE extension — 以下字段不在基础类型里
      getBuiltinEnabledState: async () => {
        try {
          const data = await apiFetch('/skills/builtin-enabled');
          return { success: true, data: data?.data || {} };
        } catch (error: any) {
          return { success: false, data: {} };
        }
      },
      saveBuiltinEnabledState: async (enabledMap: Record<string, boolean>) => {
        try {
          await apiFetch('/skills/builtin-enabled', { method: 'PUT', body: JSON.stringify({ data: enabledMap }) });
          return { success: true };
        } catch (error: any) {
          return { success: false };
        }
      },
      getAllBuiltinSkills: async (params?: { page?: number; pageSize?: number; search?: string; tag?: string; filter?: string; lang?: string }) => {
        try {
          const qs = new URLSearchParams();
          if (params?.page) qs.set('page', String(params.page));
          if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
          if (params?.search) qs.set('search', params.search);
          if (params?.tag) qs.set('tag', params.tag);
          if (params?.filter) qs.set('filter', params.filter);
          if (params?.lang) qs.set('lang', params.lang);
          const query = qs.toString();
          const data = await apiFetch(`/skills/all-builtin${query ? '?' + query : ''}`);
          return { success: true, data };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
    },

    api: {
      fetch: async (options) => {
        try {
          return await apiFetch('/proxy/fetch', { method: 'POST', body: JSON.stringify(options) });
        } catch (error: any) {
          return { ok: false, status: 0, statusText: 'Error', headers: {}, data: null, error: error.message };
        }
      },
      stream: async (options) => {
        try {
          await apiFetch('/proxy/stream', { method: 'POST', body: JSON.stringify(options) });
          return { ok: true, status: 200, statusText: 'OK' };
        } catch (error: any) {
          return { ok: false, status: 0, statusText: 'Error', error: error.message };
        }
      },
      cancelStream: async (requestId) => {
        try {
          await apiFetch('/proxy/stream/cancel', { method: 'POST', body: JSON.stringify({ requestId }) });
          return true;
        } catch {
          return false;
        }
      },
      onStreamData: (requestId, callback) => onWsEvent(`api.stream.${requestId}.data`, (data) => callback(data.chunk)),
      onStreamDone: (requestId, callback) => onWsEvent(`api.stream.${requestId}.done`, callback),
      onStreamError: (requestId, callback) => onWsEvent(`api.stream.${requestId}.error`, (data) => callback(data.error)),
      onStreamAbort: (requestId, callback) => onWsEvent(`api.stream.${requestId}.abort`, callback),
    },

    getApiConfig: async () => {
      const res = await apiFetch('/config/api');
      return res.config;
    },
    checkApiConfig: async () => {
      const res = await apiFetch('/config/api/check');
      const config = res.hasConfig ? await apiFetch('/config/api').then((r: any) => r.config) : null;
      return { hasConfig: res.hasConfig, config, error: res.error };
    },
    saveApiConfig: async (config) => {
      try {
        await apiFetch('/config/api', { method: 'PUT', body: JSON.stringify({ config }) });
        return { success: true };
      } catch (error: any) {
        return { success: false, error: error.message };
      }
    },
    generateSessionTitle: async (userInput) => {
      const res = await apiFetch('/cowork/generate-title', { method: 'POST', body: JSON.stringify({ userInput }) });
      return res.title;
    },
    getRecentCwds: async (limit) => {
      const params = limit ? `?limit=${limit}` : '';
      return apiFetch(`/cowork/recent-cwds${params}`);
    },

    ipcRenderer: {
      send: (_channel: string, ..._args: any[]) => {
        // No-op in web mode; network status can be handled differently
      },
      on: (channel: string, func: (...args: any[]) => void) => {
        // Map IPC channels to WebSocket events
        const wsEvent = channel.replace(/:/g, '.');
        return onWsEvent(wsEvent, func);
      },
    },

    window: {
      minimize: () => {},
      toggleMaximize: () => {},
      close: () => {},
      isMaximized: async () => false,
      showSystemMenu: () => {},
      onStateChanged: () => () => {},
    },

    cowork: {
      backgroundTasks: {
        list: sessionId => boundedApiFetch(`/cowork/sessions/${encodeURIComponent(sessionId)}/background-tasks`) as Promise<import('../../shared/backgroundTasks').BackgroundTaskSnapshot>,
        stop: (sessionId, taskId) => boundedApiFetch(`/cowork/sessions/${encodeURIComponent(sessionId)}/background-tasks/${encodeURIComponent(taskId)}/stop`, { method: 'POST' }) as Promise<import('../../shared/backgroundTasks').BackgroundTaskSnapshot>,
      },
      personalStorage: {
        snapshot: () => boundedApiFetch('/cowork/storage/snapshot') as Promise<import('../../shared/personalStorage').PersonalStorageSnapshot>,
        preview: () => boundedApiFetch('/cowork/storage/preview', { method: 'POST' }) as Promise<import('../../shared/personalStorage').PersonalStoragePreview>,
        cleanup: planId => boundedApiFetch('/cowork/storage/cleanup', { method: 'POST', body: JSON.stringify({ planId }) }) as Promise<import('../../shared/personalStorage').PersonalStorageCleanup>,
      },
      customAgents: {
        list: () => apiFetch('/cowork/agents'),
        create: config => apiFetch('/cowork/agents', { method: 'POST', body: JSON.stringify(config) }),
        update: (id, config, expectedRevision) => apiFetch(`/cowork/agents/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ ...config, expectedRevision }) }),
        remove: (id, expectedRevision) => apiFetch(`/cowork/agents/${encodeURIComponent(id)}`, { method: 'DELETE', body: JSON.stringify({ expectedRevision }) }),
      },
      openSecretarySession: async () => {
        return await apiFetch('/cowork/secretary/session', { method: 'POST' });
      },
      getScenarios: async (lang: string) => {
        return await apiFetch(`/cowork/scenarios?lang=${encodeURIComponent(lang)}`);
      },
      recordScenarioUsage: async (scenarioId: number) => {
        return await apiFetch('/cowork/scenarios/usage', { method: 'POST', body: JSON.stringify({ scenarioId }) });
      },
      setSessionPromptMode: async (options: { sessionId: string; promptMode: 'collaboration' | 'quick' }) => {
        try {
          await apiFetch(`/cowork/sessions/${options.sessionId}/prompt-mode`, { method: 'PUT', body: JSON.stringify({ promptMode: options.promptMode }) });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      listMemoryFiles: (scope, query = '', offset = 0) => boundedApiFetch(`/memory-sync/files/${scope}?q=${encodeURIComponent(query)}&offset=${offset}`) as Promise<import('../../shared/memoryFile').MemoryFileCatalog>,
      createMemoryFile: (scope, id, content) => boundedApiFetch(`/memory-sync/files/${scope}/${encodeURIComponent(id)}`, { method: 'POST', body: JSON.stringify({ content }) }) as Promise<import('../../shared/memoryFile').MemoryFileSnapshot>,
      archiveMemoryFile: (scope, id, expectedSha256) => boundedApiFetch(`/memory-sync/files/${scope}/${encodeURIComponent(id)}/archive`, { method: 'POST', body: JSON.stringify({ expectedSha256 }) }) as Promise<import('../../shared/memoryFile').MemoryFileSnapshot>,
      getMemoryFile: async (scope: 'secretary' | 'global' | 'soul', id: string, revision?: string) =>
        boundedApiFetch(`/memory-sync/files/${scope}/${encodeURIComponent(id)}${revision ? `?revision=${encodeURIComponent(revision)}` : ''}`) as Promise<import('../../shared/memoryFile').MemoryFileSnapshot>,
      editMemoryFile: async (scope: 'secretary' | 'global' | 'soul', id: string, input: import('../../shared/memoryFile').MemoryFileEditInput) =>
        boundedApiFetch(`/memory-sync/files/${scope}/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(input) }) as Promise<import('../../shared/memoryFile').MemoryFileSnapshot>,
      renameMemoryFile: async (scope: 'secretary' | 'global', id: string, input: import('../../shared/memoryFile').MemoryFileRenameInput) =>
        boundedApiFetch(`/memory-sync/files/${scope}/${encodeURIComponent(id)}/rename`, { method: 'POST', body: JSON.stringify(input) }) as Promise<import('../../shared/memoryFile').MemoryFileSnapshot>,
      getSecretaryMemory: async (query?: string) => {
        const qs = query ? `?q=${encodeURIComponent(query)}` : '';
        return await apiFetch(`/cowork/secretary/memory${qs}`);
      },
      forgetSecretaryMemory: async (name: string) => {
        return await apiFetch(`/cowork/secretary/memory/${encodeURIComponent(name)}/forget`, { method: 'POST' });
      },
      getConnectorTools: async (slug: string) => apiFetch(`/cowork/connectors/${encodeURIComponent(slug)}/tools`),
      getSecretaryPreferences: async () => apiFetch('/cowork/secretary/preferences'),
      saveSecretaryPreferences: async (kind, profile) => apiFetch(`/cowork/secretary/preferences/${kind}`, { method: 'PUT', body: JSON.stringify({ profile }) }),
      getSecretarySoul: async () => {
        return await apiFetch('/cowork/secretary/soul');
      },
      saveSecretarySoul: async (content: string) => {
        return await apiFetch('/cowork/secretary/soul', { method: 'PUT', body: JSON.stringify({ content }) });
      },
      resetSecretarySoul: async () => {
        return await apiFetch('/cowork/secretary/soul/reset', { method: 'POST' });
      },
      startSession: async (options) => {
        try {
          const session = await apiFetch('/cowork/sessions', { method: 'POST', body: JSON.stringify({ ...options, interactionSurface: 'web' }) });
          return { success: true, session };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      continueSession: async (options) => {
        try {
          const result = await apiFetch(`/cowork/sessions/${options.sessionId}/continue`, {
            method: 'POST',
            body: JSON.stringify({ prompt: options.prompt, scenario: (options as any).scenario, activeSkillIds: options.activeSkillIds, systemPrompt: options.systemPrompt, sandboxProfileId: (options as any).sandboxProfileId, sandboxImageId: (options as any).sandboxImageId, modelId: (options as any).modelId, interactionSurface: 'web' }),
          });
          return {
            success: true,
            queued: Boolean(result?.queued),
            item: result?.item,
          };
        } catch (error: any) {
          return {
            success: false,
            error: error.message,
            code: error.code,
            available: error.available,
            modelId: error.modelId,
          };
        }
      },
      getSessionRunState: async (sessionId) => {
        try {
          const result = await apiFetch(`/cowork/sessions/${sessionId}/run-state`);
          return {
            success: true,
            running: Boolean(result?.running),
            queued: Array.isArray(result?.queued) ? result.queued : [],
          };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      editQueuedMessage: (sessionId, id, revision, text) => boundedApiFetch(`/cowork/sessions/${encodeURIComponent(sessionId)}/queue/${encodeURIComponent(id)}`, {
        method: 'PUT', body: JSON.stringify({ revision, text }),
      }),
      steerQueuedMessage: (sessionId, id, revision) => boundedApiFetch(`/cowork/sessions/${encodeURIComponent(sessionId)}/queue/${encodeURIComponent(id)}/steer`, {
        method: 'POST', body: JSON.stringify({ revision }),
      }),
      cancelQueuedMessage: async (sessionId, turnId) => {
        try {
          await apiFetch(`/cowork/sessions/${sessionId}/queue/${turnId}`, { method: 'DELETE' });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      stopSession: async (sessionId) => {
        try {
          await apiFetch(`/cowork/sessions/${sessionId}/stop`, { method: 'POST' });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      setResultFeedback: (sessionId, messageId, feedback) => boundedApiFetch(`/cowork/sessions/${encodeURIComponent(sessionId)}/feedback`, {
        method: 'POST', body: JSON.stringify({ messageId, feedback }),
      }),
      getResultOpinionTags: sessionId => boundedApiFetch(`/cowork/sessions/${encodeURIComponent(sessionId)}/feedback/tags`),
      submitResultOpinion: (sessionId, messageId, tagCodes, content) => boundedApiFetch(`/cowork/sessions/${encodeURIComponent(sessionId)}/feedback/opinion`, {
        method: 'POST', body: JSON.stringify({ messageId, tagCodes, content }),
      }),
      deleteTurns: async (sessionId, turnUserMessageIds) => {
        try {
          return await apiFetch(`/cowork/sessions/${encodeURIComponent(sessionId)}/turns/delete`, {
            method: 'POST', body: JSON.stringify({ turnUserMessageIds }),
          });
        } catch (error) {
          return { success: false, error: error instanceof Error ? error.message : 'TURN_DELETE_FAILED' };
        }
      },
      onMessagesDeleted: callback => onWsEvent('cowork.stream.messagesDeleted', callback),
      deleteSession: async (sessionId) => {
        try {
          await apiFetch(`/cowork/sessions/${sessionId}`, { method: 'DELETE' });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      updateSessionModel: async (sessionId: string, modelId: string) => {
        try {
          await apiFetch(`/cowork/sessions/${sessionId}/model`, {
            method: 'PUT',
            body: JSON.stringify({ modelId }),
          });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      setSessionPinned: async (options) => {
        try {
          await apiFetch(`/cowork/sessions/${options.sessionId}/pin`, {
            method: 'PUT',
            body: JSON.stringify({ pinned: options.pinned }),
          });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      renameSession: async (options) => {
        try {
          await apiFetch(`/cowork/sessions/${options.sessionId}/rename`, {
            method: 'PUT',
            body: JSON.stringify({ title: options.title }),
          });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      getTurnResults: async (sessionId, turnId) => boundedApiFetch(`/cowork/sessions/${encodeURIComponent(sessionId)}/turns/${encodeURIComponent(turnId)}/results`) as Promise<import('../../shared/conversationResultManifest').ConversationResultManifest>,
      getSession: async (sessionId) => {
        try {
          const session = await apiFetch(`/cowork/sessions/${sessionId}`);
          return { success: true, session };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      getPendingPermission: async (sessionId) => {
        try {
          const result = await apiFetch(`/cowork/sessions/${sessionId}/pending-permission`);
          return { success: true, request: result?.request ?? null };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      getRegisteredDeliverables: async (sessionId) => {
        try {
          const resp = await apiFetch(`/cowork/sessions/${sessionId}/deliverables`);
          return { success: true, paths: Array.isArray(resp?.paths) ? resp.paths : [] };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      listSessions: async () => {
        try {
          const sessions = await apiFetch('/cowork/sessions');
          return { success: true, sessions };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      exportResultImage: async () => ({ success: false, error: 'Not supported in web mode' }),
      captureImageChunk: async () => ({ success: false, error: 'Not supported in web mode' }),
      saveResultImage: async (options) => {
        // Trigger browser download from base64
        try {
          const blob = await fetch(`data:image/png;base64,${options.pngBase64}`).then(r => r.blob());
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = options.defaultFileName || 'image.png';
          a.click();
          URL.revokeObjectURL(url);
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      respondToPermission: async (options) => {
        try {
          await apiFetch(`/cowork/permissions/${options.requestId}`, {
            method: 'POST',
            body: JSON.stringify({ granted: options.result }),
          });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message, expired: error.status === 404 };
        }
      },
      getConfig: async () => {
        try {
          const config = await apiFetch('/cowork/config');
          return { success: true, config };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      setConfig: async (config) => {
        try {
          await apiFetch('/cowork/config', { method: 'PUT', body: JSON.stringify(config) });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      listMemoryEntries: async (input) => {
        try {
          const params = new URLSearchParams();
          if (input.query) params.set('query', input.query);
          if (input.status) params.set('status', input.status);
          if (input.limit) params.set('limit', String(input.limit));
          if (input.offset) params.set('offset', String(input.offset));
          const entries = await boundedApiFetch<import('../types/cowork').CoworkUserMemoryEntry[]>(`/cowork/memories?${params}`);
          return { success: true, entries };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      createMemoryEntry: async (input) => {
        try {
          const entry = await boundedApiFetch<import('../types/cowork').CoworkUserMemoryEntry>('/cowork/memories', { method: 'POST', body: JSON.stringify(input) });
          return { success: true, entry };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      updateMemoryEntry: async (input) => {
        try {
          const entry = await boundedApiFetch<import('../types/cowork').CoworkUserMemoryEntry>(`/cowork/memories/${encodeURIComponent(input.id)}`, { method: 'PUT', body: JSON.stringify(input) });
          return { success: true, entry };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      deleteMemoryEntry: async (input) => {
        try {
          const result = await boundedApiFetch<{ success: boolean }>(`/cowork/memories/${encodeURIComponent(input.id)}`, { method: 'DELETE' });
          return { success: result.success === true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      getMemoryStats: async () => {
        try {
          const stats = await boundedApiFetch<import('../types/cowork').CoworkMemoryStats>('/cowork/memories/stats');
          return { success: true, stats };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      onStreamMessage: (callback) => onWsEvent('cowork.stream.message', callback),
      onStreamMessageUpdate: (callback) => onWsEvent('cowork.stream.messageUpdate', callback),
      onStreamMessageMetadata: (callback) => onWsEvent('cowork.stream.messageMetadata', callback),
      onStreamPermission: (callback) => onWsEvent('cowork.stream.permission', callback),
      onStreamDocumentExtractProgress: (callback) => onWsEvent('cowork.stream.documentExtractProgress', callback),
      onStreamPermissionResolved: (callback) => onWsEvent('cowork.stream.permissionResolved', callback),
      onStreamComplete: (callback) => onWsEvent('cowork.stream.complete', callback),
      onStreamError: (callback) => onWsEvent('cowork.stream.error', callback),
      getErrorReportStatus: async (errorId) => {
        try {
          const status = await apiFetch(`/cowork/error-reports/${encodeURIComponent(errorId)}`);
          return { success: true, status };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      retryErrorReport: async (errorId) => {
        try {
          const status = await apiFetch(`/cowork/error-reports/${encodeURIComponent(errorId)}/retry`, { method: 'POST' });
          return { success: true, status };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      onErrorReportStatusChanged: (callback) => onWsEvent('cowork.errorReport.statusChanged', callback),
      onStreamQueueUpdate: (callback) => onWsEvent('cowork.stream.queueUpdate', callback),
      onStreamUsage: (callback) => onWsEvent('cowork.stream.usage', callback),
      onStreamExecutionModeChange: (callback) => onWsEvent('cowork.stream.executionModeChange', callback),
      onStreamDeliverableRegister: (callback) => onWsEvent('cowork.stream.deliverableRegister', callback),
      onStreamDeliverableArtifact: (callback) => onWsEvent('cowork.stream.deliverableArtifact', callback),
      onSessionCreated: (callback) => onWsEvent('cowork.session_created', callback),
      generateToolQuickTasks: async () => ({ success: false, error: 'Not supported in web mode' }),
      onPlatformSandboxSynced: (callback) => onWsEvent('platform.sandbox.synced', callback),
    },

    dialog: {
      selectDirectory: async () => ({ success: false, path: null }),
      selectFile: async (options?: any) => {
        // Use a hidden <input type="file"> to let the user pick a file,
        // then upload it to the server and return the server-side path.
        return new Promise((resolve) => {
          const input = document.createElement('input');
          input.type = 'file';
          if (options?.filters?.length) {
            const exts = options.filters.flatMap((f: any) => f.extensions.map((e: string) => `.${e}`));
            input.accept = exts.join(',');
          }
          input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) {
              resolve({ success: false, path: null });
              return;
            }
            const target = { cwd: options?.cwd, executionMode: options?.executionMode, sessionId: options?.sessionId };
            try {
              // WEB-08: chunked /api/inputs upload + Host materialize (same path semantics).
              const hostPath = await uploadLegacyFileViaHostInputs({ file, target });
              resolve({ success: true, path: hostPath });
              return;
            } catch (error) {
              if (isHostInputRejection(error)) {
                // Rejected content never falls back to the deprecated upload path.
                window.dispatchEvent(new CustomEvent('app:showToast', { detail: `${file.name}：${error.message}` }));
                resolve({ success: false, path: null });
                return;
              }
              console.warn('[WebAdapter] /api/inputs upload failed, using deprecated /files/upload:', error);
            }
            try {
              // Deprecated fallback: base64 upload (media type not on the Host allowlist, etc.)
              const base64 = await new Promise<string>((res, rej) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const result = reader.result as string;
                  const idx = result.indexOf(',');
                  res(idx >= 0 ? result.slice(idx + 1) : result);
                };
                reader.onerror = () => rej(reader.error);
                reader.readAsDataURL(file);
              });
              const resp = await apiFetch('/files/upload', {
                method: 'POST',
                body: JSON.stringify({ dataBase64: base64, fileName: file.name, ...target }),
              });
              resolve({ success: true, path: resp.path });
            } catch (error: any) {
              console.error('[WebAdapter] File upload failed:', error);
              resolve({ success: false, path: null });
            }
          };
          // User cancelled the dialog
          input.addEventListener('cancel', () => {
            resolve({ success: false, path: null });
          });
          input.click();
        });
      },
      saveInlineFile: async (options) => {
        try {
          // If cwd is provided (even empty — server defaults to ~/BoClaw/workspace), save to server
          if (typeof options.cwd === 'string') {
            // WEB-08: chunked /api/inputs upload + Host materialize; the deprecated
            // base64 /files/upload stays only as a compatibility fallback.
            try {
              const mimeType = options.mimeType || 'application/octet-stream';
              const blob = await fetch(`data:${mimeType};base64,${options.dataBase64}`).then(r => r.blob());
              const file = new File([blob], options.fileName || 'upload', { type: options.mimeType || '' });
              const hostPath = await uploadLegacyFileViaHostInputs({
                file,
                target: { cwd: options.cwd, executionMode: options.executionMode, sessionId: options.sessionId },
              });
              return { success: true, path: hostPath };
            } catch (error) {
              if (isHostInputRejection(error)) return { success: false, path: null, error: error.message };
              console.warn('[WebAdapter] /api/inputs upload failed, using deprecated /files/upload:', error);
            }
            const resp = await apiFetch('/files/upload', {
              method: 'POST',
              body: JSON.stringify({
                dataBase64: options.dataBase64,
                fileName: options.fileName,
                cwd: options.cwd,
                executionMode: options.executionMode,
                sessionId: options.sessionId,
              }),
            });
            return { success: true, path: resp.path };
          }
          // Otherwise trigger a browser download
          const mimeType = options.mimeType || 'application/octet-stream';
          const blob = await fetch(`data:${mimeType};base64,${options.dataBase64}`).then(r => r.blob());
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = options.fileName || 'download';
          a.click();
          URL.revokeObjectURL(url);
          return { success: true, path: null };
        } catch (error: any) {
          return { success: false, path: null, error: error.message };
        }
      },
      readImageAsDataURL: async () => ({ success: false, dataURL: null }),
    },

    shell: {
      openPath: async () => ({ success: false, error: 'Not supported in web mode' }),
      showItemInFolder: async () => ({ success: false, error: 'Not supported in web mode' }),
      openExternal: async (url) => {
        window.open(url, '_blank');
        return { success: true };
      },
    },

    autoLaunch: {
      get: async () => ({ enabled: false }),
      set: async () => ({ success: false, error: 'Not supported in web mode' }),
    },

    appInfo: {
      getVersion: async () => {
        try {
          const res = await apiFetch('/app/version');
          return res.version;
        } catch {
          return '0.0.0';
        }
      },
      getSystemLocale: async () => {
        return navigator.language;
      },
      getDownloadUrls: async () => {
        try {
          return await apiFetch('/app/download-urls');
        } catch {
          return {};
        }
      },
    },

    im: {
      getNotificationSettings: (id, platform) => boundedApiFetch(`/im/instances/${encodeURIComponent(id)}/notifications/${encodeURIComponent(platform)}`),
      setNotificationTarget: (id, platform, targetId) => boundedApiFetch(`/im/instances/${encodeURIComponent(id)}/notifications/${encodeURIComponent(platform)}`, { method: 'PUT', body: JSON.stringify({ targetId }) }),
      startPairing: id => boundedApiFetch(`/im/instances/${encodeURIComponent(id)}/pairing`, { method: 'POST' }),
      pollPairing: (id, pairingId) => boundedApiFetch(`/im/instances/${encodeURIComponent(id)}/pairing/${encodeURIComponent(pairingId)}`),
      cancelPairing: (id, pairingId) => boundedApiFetch(`/im/instances/${encodeURIComponent(id)}/pairing/${encodeURIComponent(pairingId)}`, { method: 'DELETE' }),
      listInstances: () => apiFetch('/im/instances'),
      createInstance: (name, platform) => apiFetch('/im/instances', { method: 'POST', body: JSON.stringify({ name, platform }) }),
      deleteInstance: id => apiFetch(`/im/instances/${encodeURIComponent(id)}`, { method: 'DELETE' }),
      getConfig: async (instanceId = 'default') => {
        try {
          const config = await apiFetch(`/im/config?instanceId=${encodeURIComponent(instanceId)}`);
          return { success: true, config };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      setConfig: async (config, instanceId = 'default') => {
        try {
          await apiFetch(`/im/config?instanceId=${encodeURIComponent(instanceId)}`, { method: 'PUT', body: JSON.stringify(config) });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      startGateway: async (platform, instanceId = 'default') => {
        try {
          await apiFetch(`/im/gateway/${platform}/start?instanceId=${encodeURIComponent(instanceId)}`, { method: 'POST' });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      stopGateway: async (platform, instanceId = 'default') => {
        try {
          await apiFetch(`/im/gateway/${platform}/stop?instanceId=${encodeURIComponent(instanceId)}`, { method: 'POST' });
          return { success: true };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      testGateway: async (platform, configOverride, instanceId = 'default') => {
        try {
          const res = await apiFetch(`/im/gateway/${platform}/test?instanceId=${encodeURIComponent(instanceId)}`, {
            method: 'POST',
            body: JSON.stringify({ config: configOverride }),
          });
          return { success: true, result: res };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      getStatus: async (instanceId = 'default') => {
        try {
          const status = await apiFetch(`/im/status?instanceId=${encodeURIComponent(instanceId)}`);
          return { success: true, status };
        } catch (error: any) {
          return { success: false, error: error.message };
        }
      },
      onStatusChange: (callback, instanceId = 'default') => onWsEvent('im.status.change', packet => {
        if (packet?.instanceId === instanceId || (instanceId === 'default' && String(packet?.instanceId ?? '').startsWith('default-'))) callback(packet.status);
        else if (!packet?.instanceId && instanceId === 'default') callback(packet);
      }),
      onMessageReceived: (callback) => onWsEvent('im.message.received', callback),
    },

    scheduledTasks: {
      list: async () => {
        const tasks = await apiFetch('/scheduled-tasks');
        return { success: true, tasks };
      },
      get: async (id) => {
        const task = await apiFetch(`/scheduled-tasks/${id}`);
        return { success: true, task };
      },
      create: async (input) => {
        const task = await apiFetch('/scheduled-tasks', { method: 'POST', body: JSON.stringify(input) });
        return { success: true, task };
      },
      update: async (id, input) => {
        const task = await apiFetch(`/scheduled-tasks/${id}`, { method: 'PUT', body: JSON.stringify(input) });
        return { success: true, task };
      },
      delete: async (id) => {
        await apiFetch(`/scheduled-tasks/${id}`, { method: 'DELETE' });
        return { success: true };
      },
      toggle: async (id, enabled) => {
        const result = await apiFetch(`/scheduled-tasks/${id}/toggle`, { method: 'POST', body: JSON.stringify({ enabled }) });
        return { success: true, task: result.task || result, warning: result.warning };
      },
      runManually: async (id) => {
        await apiFetch(`/scheduled-tasks/${id}/run`, { method: 'POST' });
        return { success: true };
      },
      stop: async (id) => {
        await apiFetch(`/scheduled-tasks/${id}/stop`, { method: 'POST' });
        return { success: true };
      },
      listRuns: async (taskId, limit, offset) => {
        const params = new URLSearchParams();
        if (limit) params.set('limit', String(limit));
        if (offset) params.set('offset', String(offset));
        const runs = await apiFetch(`/scheduled-tasks/${taskId}/runs?${params}`);
        return { success: true, runs };
      },
      countRuns: async (taskId) => {
        const result = await apiFetch(`/scheduled-tasks/${taskId}/runs/count`);
        return { success: true, count: result.count };
      },
      listAllRuns: async (limit, offset) => {
        const params = new URLSearchParams();
        if (limit) params.set('limit', String(limit));
        if (offset) params.set('offset', String(offset));
        const runs = await apiFetch(`/scheduled-tasks/runs/all?${params}`);
        return { success: true, runs };
      },
      clearRunHistory: async () => {
        return await apiFetch('/scheduled-tasks/runs/all', { method: 'DELETE' });
      },
      onStatusUpdate: (callback) => onWsEvent('scheduledTask.statusUpdate', callback),
      onRunUpdate: (callback) => onWsEvent('scheduledTask.runUpdate', callback),
    },

    permissions: {
      checkCalendar: async () => ({ success: false, error: 'Not supported in web mode' }),
      requestCalendar: async () => ({ success: false, error: 'Not supported in web mode' }),
    },

    networkStatus: {
      send: () => {},
    },
  };
}
